// The night sky: ~500 real stars at their true positions for tonight over Warsaw
// (starSky.mjs), turning left to right through the game's night, plus at most one shooting
// star per night. Stars move ~3.5 px/s across a 2-screen-wide sky (starSky.mjs skyDateFor), so they are recomputed and
// redrawn every frame (a 10 Hz redraw made them visibly step). Graphics are not pixel-rounded.
import { STARS } from './starCatalog.mjs';
import { starsAt, skyDateFor, starStyle, shootingStarFor, meteorDue } from './starSky.mjs';
import { starAlpha } from './skyClock.mjs';
import { skyToScreen } from './backdropLayout.mjs';
import { DEPTH } from './backdropDepth.mjs';

export const STAR_SPREAD = 2;     // the south window (azimuth 90°..270°) spans two screen widths
export const STAR_FACTOR = 0.02;
const TAIL_PX = 90;

// `onMeteor`: called when a shooting star starts (the paintings' hook, house parts' plan, part 3).
export function createStarLayer(scene, { onMeteor } = {}) {
  const g = scene.add.graphics().setScrollFactor(0).setDepth(DEPTH.stars);
  const m = scene.add.graphics().setScrollFactor(0).setDepth(DEPTH.meteor);
  const styles = STARS.map(([, , mag, bv]) => starStyle(mag, bv));
  const twinklePhase = STARS.map((_, i) => (i * 2.399963) % (Math.PI * 2));
  let prevT = null;
  let meteor = null;      // { spec, start }
  let forced = false;

  // The star dome tracks the horizon like the sun and moon, so stars rise and set at the hills.
  const geo = (ctx) => ({ horizonY: ctx.horizonY, viewW: ctx.viewW, panX: ctx.panX, factor: STAR_FACTOR, spread: STAR_SPREAD });

  const drawStars = (ctx, a) => {
    g.clear();
    if (a <= 0.01) return;
    const o = geo(ctx);
    for (const s of starsAt(skyDateFor(ctx.t, ctx.now), STARS)) {
      const p = skyToScreen(s.u, s.v, o);
      if (p.x < -4 || p.x > ctx.viewW + 4 || p.y > ctx.horizonY || p.y < -4) continue;   // below the real horizon: behind the land
      const st = styles[s.i];
      const tw = 0.85 + 0.15 * Math.sin(ctx.now / 600 + twinklePhase[s.i]);
      g.fillStyle(st.color, a * st.alpha * tw);
      if (st.shape === 'circle') g.fillCircle(p.x, p.y, st.r);
      else g.fillRect(p.x - st.r, p.y - st.r, st.r * 2, st.r * 2);
    }
  };

  const drawMeteor = (ctx) => {
    m.clear();
    if (!meteor) return;
    const f = (ctx.now - meteor.start) / meteor.spec.durationMs;
    if (f >= 1) { meteor = null; return; }
    const s = meteor.spec;
    const p0 = skyToScreen(s.u, s.v, geo(ctx));
    const L = s.length * ctx.viewW * STAR_SPREAD;
    const ang = (s.angleDeg * Math.PI) / 180;
    const dx = Math.cos(ang), dy = Math.sin(ang);
    const head = f * L;
    const tail = Math.min(head, TAIL_PX);
    const fade = f < 0.8 ? 1 : (1 - f) / 0.2;
    for (let k = 0; k < 6; k++) {
      const a0 = head - (tail * k) / 6, a1 = head - (tail * (k + 1)) / 6;
      m.lineStyle(2 - k * 0.25, 0xffffff, fade * (1 - k / 6));
      m.lineBetween(p0.x + dx * a0, p0.y + dy * a0, p0.x + dx * a1, p0.y + dy * a1);
    }
  };

  return {
    update(ctx) {
      const a = starAlpha(ctx.t);
      drawStars(ctx, a);
      const spec = shootingStarFor(ctx.night);
      if (forced || meteorDue(prevT, ctx.t, spec)) {
        meteor = { spec: spec ?? { t: ctx.t, u: 0.4, v: 0.8, angleDeg: 35, length: 0.12, durationMs: 800 }, start: ctx.now };
        forced = false;
        onMeteor?.();
      }
      prevT = ctx.t;
      drawMeteor(ctx);
    },
    debugMeteor() { forced = true; },
    destroy() { g.destroy(); m.destroy(); },
  };
}
